import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowSquareOut, CheckCircle, Prohibit } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { adminApi, getErrorMessage } from "@/lib/api";
import { LinesTable } from "./LinesTable";
import { FaultList } from "./FaultList";
import { Pill, Stat, aud, btnGhost, btnPrimary, selectCls } from "./ui";

function ClaimActions({ c, onSaved }) {
  const [note, setNote] = useState(c.review_note || "");
  const [amount, setAmount] = useState(c.approved_amount_override ?? "");
  const [busy, setBusy] = useState(false);
  const save = async (patch, msg) => {
    setBusy(true);
    try { onSaved((await adminApi.patch(`/admin/wi/claims/${c.id}`, patch)).data); toast.success(msg); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2.5 border border-neutral-800 rounded-xl p-4" data-testid="wi-claim-actions">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[0.7rem] text-neutral-500">Correct approved amount (AUD inc GST)
          <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={`${selectCls} block w-40 mt-1`} data-testid="wi-claim-amount-input" />
        </label>
        <button className={btnGhost} disabled={busy || amount === ""} onClick={() => save({ approved_amount_override: Number(amount) }, "Amount corrected")} data-testid="wi-claim-amount-save">Save amount</button>
        {c.approved_amount_override !== undefined && c.approved_amount_override !== null && (
          <button className="text-xs text-neutral-500 hover:text-white" onClick={() => { setAmount(""); save({ clear_amount_override: true }, "Correction removed"); }}>Remove correction</button>
        )}
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} placeholder="Review note (documents the exception)" data-testid="wi-claim-note"
        className="w-full bg-black/40 border border-neutral-800 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#73bf44] placeholder:text-neutral-600" />
      <div className="flex flex-wrap gap-2">
        <button className={btnPrimary} disabled={busy} onClick={() => save({ reviewed: !c.reviewed, review_note: note }, c.reviewed ? "Marked as not reviewed" : "Marked as reviewed")} data-testid="wi-claim-review-btn">
          <CheckCircle size={14} weight="fill" /> {c.reviewed ? "Un-mark reviewed" : "Mark reviewed"}
        </button>
        <button className={btnGhost} disabled={busy} onClick={() => save({ excluded: !c.excluded, review_note: note }, c.excluded ? "Included again" : "Excluded from benchmarks & statistics")} data-testid="wi-claim-exclude-btn">
          <Prohibit size={14} /> {c.excluded ? "Include again" : "Exclude from analysis"}
        </button>
      </div>
    </div>
  );
}

export function ClaimDrawer({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => adminApi.get(`/admin/wi/claims/${id}`).then(({ data }) => setData(data)).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);
  const changed = (d) => { if (d?.claim) setData(d); else load(); onChanged?.(); };
  const c = data?.claim;
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="bg-[#0A0A0A] border-neutral-800 text-neutral-200 w-full sm:max-w-3xl overflow-y-auto" data-testid="wi-claim-drawer">
        {!c ? <><SheetTitle className="sr-only">Loading claim</SheetTitle><SheetDescription className="sr-only">Loading claim details</SheetDescription><div className="h-40 animate-pulse" /></> : (
          <div className="space-y-5">
            <SheetHeader>
              <SheetTitle className="font-heading text-white text-left flex items-center gap-2 flex-wrap">
                {c.otma || "No OTMA number"} <span className="font-mono text-sm text-neutral-400">{c.chassis}</span>
                {c.reviewed && <Pill tone="green">Reviewed</Pill>}{c.excluded && <Pill tone="red">Excluded</Pill>}
              </SheetTitle>
              <SheetDescription className="text-left text-neutral-500">
                {c.source === "w2" ? "Warranty Claim 2.0" : "Approval register only"} · {c.model || c.model_family} · {c.dealer} · {c.claim_date} · {c.status}
              </SheetDescription>
            </SheetHeader>
            {c.monday_url && <a href={c.monday_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#73bf44] hover:underline" data-testid="wi-claim-monday-link"><ArrowSquareOut size={13} /> Open original claim in Monday.com</a>}
            <div className="flex flex-wrap gap-1.5">{Object.entries(c.flag_labels || {}).map(([k, l]) => <Pill key={k} tone="amber">{l}</Pill>)}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Stat label="Final amount" value={aud(c.final_amount, 2)} sub={c.final_amount_basis ? `from ${c.final_amount_basis}` : "not available"} tone="gold" />
              <Stat label="Quoted total" value={aud(c.quoted_total, 2)} sub={c.quoted_total_text || "—"} />
              <Stat label="Repair orders" value={c.repair_orders.length} sub={`${c.photo_count} photos`} />
            </div>
            {c.warranty_issues && <p className="text-sm text-neutral-300 whitespace-pre-wrap bg-black/30 rounded-xl p-3 border border-neutral-800">{c.warranty_issues}</p>}
            {c.extraction?.issues?.length > 0 && (
              <ul className="text-xs text-amber-300/90 list-disc pl-5 space-y-0.5" data-testid="wi-claim-extraction-issues">{c.extraction.issues.map((x, i) => <li key={i}>{x}</li>)}</ul>
            )}
            <ClaimActions c={c} onSaved={changed} />
            <div><div className="text-[0.62rem] uppercase tracking-[0.16em] font-bold text-neutral-500 mb-2">Repair lines</div><LinesTable lines={data.lines} onSaved={changed} showClaim={false} /></div>
            <div><div className="text-[0.62rem] uppercase tracking-[0.16em] font-bold text-neutral-500 mb-2">Faults (reliability)</div><FaultList faults={data.faults} onSaved={changed} /></div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
